import { queryClient } from '@/config/query/queryClient'
import { useAccountContext } from '@/context/account'
import type { ClaimRewards } from '@/queries/claimRewards.query'
import { claimRewardsQueryKey } from '@/queries/claimRewards.query'
import { rewardsQueryKey } from '@/queries/rewards.query'
import { invalidateQueriesGlobally } from '@/utils/queries/invalidateQueriesGlobally'
import type { Network } from '@vaultsfyi/common/types'
import { getChainId } from '@vaultsfyi/common/utils'
import { type Address, type Hex, UserRejectedRequestError } from 'viem'
import { useConfig } from 'wagmi'
import { getCallsStatus, sendCalls, sendTransaction, switchChain, waitForTransactionReceipt } from 'wagmi/actions'
import { useBatchSupported } from './useBatchSupported'

interface ClaimTransactionsResult {
  network: string
  txHash: string | undefined
}

interface UseClaimRewardsTransactionProps {
  claimActions: ClaimRewards
  setTransactionsFinished: (result: ClaimTransactionsResult[]) => void
  setNetworkTxLoading?: (network: string) => void
  setNetworkTxSuccess?: (network: string) => void
  setNetworkTxError?: (network: string, error: string) => void
}

export function useClaimRewardsTransactions({
  claimActions,
  setTransactionsFinished,
  setNetworkTxLoading,
  setNetworkTxSuccess,
  setNetworkTxError,
}: UseClaimRewardsTransactionProps) {
  const wagmiConfig = useConfig()
  const { chainId: connectedChainId } = useAccountContext()

  const possibleNetworks: Network[] = ['mainnet', 'arbitrum', 'base', 'worldchain']
  const batchCheckers = possibleNetworks.reduce(
    (acc, network) => {
      const { checkBatchSupported } = useBatchSupported(network)
      acc[network] = checkBatchSupported
      return acc
    },
    {} as Record<string, () => Promise<boolean>>
  )

  const getTxsCount = async () => {
    if (!claimActions) return 0

    let count = 0
    for (const [network, { actions }] of Object.entries(claimActions)) {
      const checker = batchCheckers[network]
      const isBatchSupported = checker ? await checker() : false
      count += isBatchSupported ? 1 : actions.length
    }
    return count
  }

  async function executeTxs() {
    const results: ClaimTransactionsResult[] = []

    try {
      if (!claimActions) {
        throw new Error('No claim actions found')
      }

      for (const [network, { actions }] of Object.entries(claimActions)) {
        const txChainId = getChainId(network)
        const checker = batchCheckers[network]
        const isBatchSupported = checker ? await checker() : false

        setNetworkTxLoading?.(network)

        try {
          if (connectedChainId !== txChainId) {
            await switchChain(wagmiConfig, { chainId: txChainId })
          }

          if (isBatchSupported) {
            const calls = actions.map((action) => ({
              to: action.tx.to as Address,
              data: action.tx.data as Hex | undefined,
              value: action.tx.value ? BigInt(action.tx.value) : undefined,
              chainId: action.tx.chainId,
            }))

            const { id: batchId } = await sendCalls(wagmiConfig, { calls })
            let callsStatus = await getCallsStatus(wagmiConfig, { id: batchId })
            while (callsStatus.status === 'pending') {
              await new Promise((resolve) => setTimeout(resolve, 300))
              callsStatus = await getCallsStatus(wagmiConfig, { id: batchId })
            }
            results.push({ network, txHash: undefined })
            setNetworkTxSuccess?.(network)
          } else {
            let lastTxHash: string | undefined
            for (let i = 0; i < actions.length; i++) {
              const tx = actions[i].tx
              if (!tx) throw new Error(`No transaction found for step ${i} on ${network}`)

              const hash = await sendTransaction(wagmiConfig, {
                to: tx.to as Address,
                data: tx.data as Hex,
                chainId: tx.chainId,
                value: tx.value ? BigInt(tx.value) : undefined,
              })
              await waitForTransactionReceipt(wagmiConfig, { hash })
              lastTxHash = hash
            }
            results.push({ network, txHash: lastTxHash })
            setNetworkTxSuccess?.(network)
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          setNetworkTxError?.(network, errorMessage)
          throw error
        }
      }

      setTransactionsFinished(results)

      await Promise.all([
        queryClient.resetQueries({ queryKey: rewardsQueryKey }),
        queryClient.resetQueries({ queryKey: claimRewardsQueryKey }),
      ])

      await invalidateQueriesGlobally()
    } catch (error) {
      if (
        error instanceof UserRejectedRequestError ||
        (error as { shortMessage?: string })?.shortMessage === 'User rejected the request.'
      ) {
        throw error
      }
      throw error
    }
  }

  return { getTxsCount, executeTxs }
}
